import { format } from 'date-fns';

const toUTCDate = (dateString: string | number | Date): Date => {
  const d = new Date(dateString);
  return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
};

export interface DateTimeObject {
  dateFrom?: string | number | Date;
  dateTo?: string | number | Date;
}

export const formatDateTime = (obj: DateTimeObject | DateTimeObject[]): string | undefined => {
  let targetObj: DateTimeObject;
  if (Array.isArray(obj)) {
    targetObj = obj[0];
  } else {
    targetObj = obj;
  }

  if (targetObj.dateFrom && targetObj.dateTo) {
    const { dateFrom, dateTo } = targetObj;
    const momentDateFrom = toUTCDate(dateFrom);
    const momentDateTo = toUTCDate(dateTo);
    const dateFormat = 'd MMMM yyyy';
    const timeFormat = 'h:mmaaa';
    const info = {
      from: {
        date: format(momentDateFrom, dateFormat),
        time: format(momentDateFrom, timeFormat),
      },
      to: {
        date: format(momentDateTo, dateFormat),
        time: format(momentDateTo, timeFormat),
      },
    };
    const sameChecker = (from: string, to: string) =>
      from && to && from === to ? from : `${from} - ${to}`;
    const minFormat = (time: string) => (time.includes(':00') ? time.replace(':00', '') : time);
    return `${sameChecker(info.from.date, info.to.date)}, ${sameChecker(
      minFormat(info.from.time),
      minFormat(info.to.time),
    )}`;
  }
  return undefined;
};

export const formatDate = (date: string | number | Date): string => {
  const day = toUTCDate(date);
  const dateFormat = 'd MMMM yyyy';
  const dateFormatted = format(day, dateFormat);

  return dateFormatted;
};

/* istanbul ignore next -- @preserve */
export interface JoinArrItem {
  description?: string;
}

/* istanbul ignore next -- @preserve */
export const joinArr = (type?: JoinArrItem[], topic?: JoinArrItem[], separator = ', '): string => {
  /* istanbul ignore next -- @preserve */
  if (!type && !topic) return '';

  /* istanbul ignore next -- @preserve */
  const genArr = (arr: JoinArrItem[]) =>
    arr && arr.map((item) => (item?.description ? item.description.replace(/-/g, ' ') : ''));

  if (type || topic) {
    const typeArr = type ? genArr(type) : [];
    const topicArr = topic ? genArr(topic) : [];
    const newArr = [...typeArr, ...topicArr];

    return newArr.join(separator);
  }
  return '';
};

export const generateQueryString = (obj: Record<string, any> = {}): string => {
  // removes unneeded object keys / when generating query string
  Object.keys(obj).forEach(
    (key) =>
      (obj[key] === undefined || obj[key] === null || obj[key] === '' || obj[key].length === 0) &&
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete obj[key],
  );

  const constantFilters = [
    'partner',
    'topic',
    'type',
    'category',
    'region',
    'location',
    'scope',
    'articleType',
  ];

  const queryString = Object.keys(obj)
    .map((key) => {
      if (Array.isArray(obj[key])) {
        if (constantFilters.includes(key)) {
          const arr: any[] = [];
          obj[key].forEach((item: any) => arr.push(item?.id || item?.key));

          return `${key}=${arr.join()}`;
        }
        return '';
      }

      if (typeof obj[key] === 'string') {
        if (key === 'search') {
          return `${key}=${encodeURI(obj[key])}`;
        }
        return `${key}=${obj[key].replace(/\s/g, '')}`;
      }
      if (typeof obj[key] === 'number' || typeof obj[key] === 'boolean') {
        return `${key}=${obj[key]}`;
      }
      /* istanbul ignore next -- @preserve */
      return undefined;
    })
    .filter((v) => v !== undefined)
    .join('&');

  return queryString.length > 1 ? `?${queryString}` : '';
};

export const objectForReq = (obj: Record<string, any>): Record<string, any> => {
  const copyObj = { ...obj };

  Object.entries(copyObj).forEach((item) => {
    if (Array.isArray(item[1])) {
      const arr: any[] = [];
      item[1].forEach((value: any) => arr.push(value?.id || value?.key));
      copyObj[item[0]] = arr.join();
    }
  });

  return copyObj;
};

export const queryStringToObj = (
  query: URLSearchParams | Map<string, string>,
): Record<string, any> =>
  [...query.entries()].reduce((acc: Record<string, any>, item) => {
    const [key, value] = item;

    if (Object.prototype.hasOwnProperty.call(acc, key)) {
      if (Array.isArray(acc[key])) {
        acc[key] = [...acc[key], value];
      } else {
        acc[key] = [acc[key], value];
      }
    } else {
      acc[key] = value;
    }

    return acc;
  }, {});

export const reduceData = ({ arr, value }: { arr: any[]; value: string }): any[] =>
  arr.map((item) =>
    Object.keys(item).reduce((object: Record<string, any>, key) => {
      if (key !== value) {
        object[key] = item[key];
      }
      return object;
    }, {}),
  );

/**
 * Article listing pagination + query-arg helpers.
 *
 * These are shared between the client-side ArticleListing tree and the
 * server-side SSR prefetch (entry-server.jsx) so that the cache key built on
 * the server always matches the one the client hydrates against.
 */

export const DEFAULT_ARTICLES_LIMIT = 12;

// The shape of a single filter-group option as passed down through the CMS
// component payload (e.g. one entry of `region`/`category`/`articleType`).
// `objectForReq` reads `.id`/`.key` off each item when joining these into
// the request body.
export interface ArticleFilterOptionRef {
  id?: string;
  key?: string;
}

export interface ArticleListingCmsProps {
  partner?: string;
  region?: ArticleFilterOptionRef[];
  location?: ArticleFilterOptionRef[];
  scope?: ArticleFilterOptionRef[];
  topic?: ArticleFilterOptionRef[];
  category?: ArticleFilterOptionRef[];
  orderBy?: string;
  type?: string;
  articleType?: ArticleFilterOptionRef[];
  defaultPageSize?: number;
}

// Mirrors the CMS-facing prop contract of ArticleListing - do not rename these
// keys, they are relied on by the existing Tridion component configuration.
export const buildArticleConstantFilters = (props: ArticleListingCmsProps = {}) => ({
  partner: props.partner || '',
  regionIds: props.region || '',
  office: props.location || '',
  scopeIds: props.scope || '',
  topic: props.topic || '',
  category: props.category || '',
  orderBy: props.orderBy || '',
  group: props.type || '',
  articleType: props.articleType || '',
  pageSize: props.defaultPageSize || DEFAULT_ARTICLES_LIMIT,
});

export interface ArticlesPagination {
  offset: number;
  limit: number;
}

const toPositiveInt = (value: string | null): number | undefined => {
  if (value === null) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : undefined;
};

export const parseArticlesPagination = (
  searchParams: URLSearchParams,
  defaultLimit: number = DEFAULT_ARTICLES_LIMIT,
): ArticlesPagination => ({
  // `?? ` (not `||`): toPositiveInt already returns 0 for an explicit,
  // valid "0" - `||` would discard that (0 is falsy) and silently
  // substitute the default instead of honouring what was actually asked for.
  offset: toPositiveInt(searchParams.get('offset')) ?? 0,
  limit: toPositiveInt(searchParams.get('limit')) ?? defaultLimit,
});

// Round-trips filter state through the URL query string using the same
// generic key/value model as generateQueryString, so CMS-configured filter
// groups (e.g. custom archived/current toggle keys) keep working without
// this helper needing to know their names up front.
export const parseArticlesAppliedFilters = (searchParams: URLSearchParams): Record<string, any> => {
  const queryStringObj = queryStringToObj(searchParams);
  const paginationKeys = ['offset', 'limit'];

  return Object.keys(queryStringObj)
    .filter((key) => !paginationKeys.includes(key))
    .reduce((acc: Record<string, any>, key) => {
      const rawValue = queryStringObj[key];
      // A repeated query-string key (e.g. "?search=a&search=b") becomes an
      // array here (see queryStringToObj). Only `category` is meant to
      // carry multiple values, as a single comma-joined param - every
      // other key takes just the first occurrence rather than silently
      // joining into a corrupted value (e.g. "a,b" for a duplicated search).
      const value = Array.isArray(rawValue) && key !== 'category' ? rawValue[0] : rawValue;

      if (key === 'category') {
        acc[key] = String(value)
          .split(',')
          .filter(Boolean)
          .map((id) => ({ id }));
        return acc;
      }

      if (key === 'search') {
        // queryStringToObj (URLSearchParams-based) already decodes this
        // once - decoding it again here would both be redundant and throw
        // on a search term containing a literal "%" (e.g. "50% off").
        acc[key] = String(value);
        return acc;
      }

      if (value === 'true' || value === 'false') {
        acc[key] = value === 'true';
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {});
};

// Shared by ArticleFilters (reset-link visibility) and ArticleResults
// (whether to show the featured-article treatment) so the two components
// can never disagree about whether a filter is "active". Checks key
// presence, not truthiness: some filters (e.g. "current") default true on
// the backend, so an explicit `false` in the URL is still an active,
// user-made choice - only a key that's missing entirely means untouched.
export const hasActiveArticleFilters = (appliedFilters: Record<string, any> = {}): boolean =>
  !!appliedFilters?.search ||
  !!appliedFilters?.category?.length ||
  Object.keys(appliedFilters).some(
    (key) => !['search', 'category'].includes(key) && appliedFilters[key] !== undefined,
  );

export interface BuildArticlesQueryArgsParams {
  endpoint: string;
  /** Route pathname - scopes the RTK Query cache entry to this page. */
  instanceKey: string;
  cmsContent: ArticleListingCmsProps & { disableFeatured?: boolean };
  searchParams: URLSearchParams;
  context?: unknown;
}

// Single source of truth for building getArticlesPage's query args, used by
// both the client (ArticleListing.jsx) and the SSR prefetch
// (prefetchArticleListings.ts). Those two call sites used to hand-assemble
// this object independently - if they ever drifted, the client and server
// would compute different RTK Query cache keys, defeating SSR prefetch
// reuse (a hydration mismatch + duplicate fetch, the very bug this
// migration exists to fix).
export const buildArticlesQueryArgs = ({
  endpoint,
  instanceKey,
  cmsContent,
  searchParams,
  context,
}: BuildArticlesQueryArgsParams) => {
  const constantFilters = buildArticleConstantFilters(cmsContent);
  const appliedFilters = parseArticlesAppliedFilters(searchParams);
  const { offset, limit } = parseArticlesPagination(searchParams, constantFilters.pageSize);

  // Whether *this specific request* should ask for one extra article: the
  // featured/hero slot is sliced off the front of the results before the
  // grid renders, so the grid still ends up with `limit` cards. Only true
  // for the true first page of an unfiltered session - a Load More
  // request (offset > 0) must not re-request the extra item, or every
  // page after the first would also be off by one.
  const withFeaturedSlot =
    !cmsContent.disableFeatured && offset === 0 && !hasActiveArticleFilters(appliedFilters);

  return {
    endpoint,
    instanceKey,
    constantFilters,
    appliedFilters,
    offset,
    limit,
    withFeaturedSlot,
    context,
  };
};

export interface CalculateNextArticlesOffsetArgs {
  /** The offset the current browsing session actually started from (may be
   *  non-zero for a deep link into a crawlable pagination URL). */
  sessionStartOffset: number;
  /** How many articles are currently loaded/shown in this session. */
  loadedCount: number;
  /** Total matching articles for the current filters. */
  total: number;
}

// "Load More" walks the collection as a circular buffer starting from
// wherever the session began, wrapping back to 0 once it runs past the
// end - rather than a plain running offset, which would walk off the end
// of the collection (and into invalid/out-of-range pageStart values) for
// any session that didn't start at offset 0. For a session that DID start
// at 0, the modulo is a no-op and this is identical to a plain running
// offset. Callers should stop showing "Load More" once loadedCount reaches
// total - every article will have been shown exactly once by then.
export const calculateNextArticlesOffset = ({
  sessionStartOffset,
  loadedCount,
  total,
}: CalculateNextArticlesOffsetArgs): number => {
  if (total <= 0) return 0;
  return (sessionStartOffset + loadedCount) % total;
};

export interface BuildArticlesPageHrefArgs {
  pathname: string;
  appliedFilters?: Record<string, any>;
  offset: number;
  limit: number;
}

// Builds a crawlable, shareable URL for a given page of results - used for
// both the "Load More" anchor href and browser history updates. Built with
// URLSearchParams rather than generateQueryString so reserved characters
// (`&`, `#`, ...) in a search term are fully percent-encoded - otherwise a
// search like "funds & pensions" would split the URL into extra params and
// a "#" would drop the pagination params entirely.
export const buildArticlesPageHref = ({
  pathname,
  appliedFilters = {},
  offset,
  limit,
}: BuildArticlesPageHrefArgs): string => {
  const params = new URLSearchParams();

  Object.entries(appliedFilters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length) {
        params.set(key, value.map((item) => item?.id ?? item).join(','));
      }
    } else if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  params.set('offset', String(offset));
  params.set('limit', String(limit));

  return `${pathname}?${params.toString()}`;
};

export interface BuildArticlesResultsTextArgs {
  total: number;
}

export interface ArticlesResultsSummary {
  count: number;
  /** Everything after the (separately emphasised) count, e.g. `Articles`. */
  description: string;
}

// Matches the pre-rebuild production text ("{total} articles") - a
// current/archived/category/search-aware version was tried during this
// rebuild but wasn't something the client asked for, so it was reverted.
export const buildArticlesResultsText = ({
  total,
}: BuildArticlesResultsTextArgs): ArticlesResultsSummary => {
  const articleWord = total === 1 ? 'article' : 'articles';

  return {
    count: total,
    description: articleWord,
  };
};
